/**
 * Mine vurderinger — oversiktsside (Fase A).
 *
 * Viser alle vurderinger kandidaten har gjennomført, med:
 * - Default-filter: siste 12 måneder + alle arkiverte
 * - Toggle for å også vise eldre (utgåtte retention)
 * - Hurtigvalg til detaljside per vurdering
 *
 * Retention-regel: vurderinger synes i 1 år fra finalize_at.
 * Etter det må de arkiveres av kandidat for å beholdes.
 */
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ClipboardCheck,
  CheckCircle2,
  XCircle,
  Archive,
  Clock,
  ChevronRight,
  Info,
  Loader2,
  ArchiveRestore,
} from 'lucide-react';
// Deep-link auth-helper: ved 401 send til login med returnTo=current path
function redirectToLogin(): void {
  const current = window.location.pathname + window.location.search;
  window.location.href = `/?returnTo=${encodeURIComponent(current)}`;
}

interface AssessmentListItem {
  id: string;
  course_instance_id: string;
  course_name: string;
  study_plan_title: string;
  organization_name: string | null;
  model: 'single_component' | 'weighted_composite' | 'all_required_composite';
  component_count: number;
  graded_count: number;
  passed: boolean;
  finalized_at: string;
  archived: boolean;
  archived_at: string | null;
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('nb-NO', {
      timeZone: 'Europe/Oslo',
      dateStyle: 'long',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function modelLabel(m: string): string {
  if (m === 'single_component') return 'Enkel vurdering';
  if (m === 'weighted_composite') return 'Kompositt (vektet)';
  if (m === 'all_required_composite') return 'Kompositt (alle må bestås)';
  return m;
}

export function AssessmentsListPage() {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState<AssessmentListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeArchived]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const url = `/api/candidate/assessments${includeArchived ? '?include_archived=1' : ''}`;
      const res = await fetch(url, { credentials: 'include' });
      if (res.status === 401) { redirectToLogin(); return; }
      const data = await res.json();
      if (!res.ok || !data.success) {
        setError(data.message || 'Kunne ikke hente vurderinger');
        return;
      }
      setAssessments(data.assessments || []);
    } catch (err) {
      console.error(err);
      setError('Kunne ikke hente vurderinger');
    } finally {
      setLoading(false);
    }
  }

  async function toggleArchive(id: string, currentlyArchived: boolean) {
    try {
      const url = `/api/candidate/assessments/${id}/archive`;
      const method = currentlyArchived ? 'DELETE' : 'POST';
      const res = await fetch(url, { method, credentials: 'include' });
      if (res.status === 401) { redirectToLogin(); return; }
      if (!res.ok) throw new Error('Kunne ikke endre arkiv-status');
      await load();
    } catch (err) {
      console.error(err);
      alert('Kunne ikke endre arkiv-status');
    }
  }

  const active = assessments.filter(a => !a.archived);
  const archived = assessments.filter(a => a.archived);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 md:py-8">
      <div className="mb-6 md:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-kf-blue-100 rounded-lg">
            <ClipboardCheck className="w-6 h-6 text-kf-blue-700" />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-kf-gray-900">Mine vurderinger</h1>
        </div>
        <p className="text-kf-gray-600">
          Oversikt over prøver, prosjekter og andre vurderinger du har gjennomført.
        </p>
      </div>

      <div className="mb-6 p-4 bg-kf-blue-50 border border-kf-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-kf-blue-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-kf-gray-700">
            <p className="font-semibold mb-1">Om visningen</p>
            <ul className="list-disc pl-5 space-y-0.5">
              <li>Vurderinger vises her i 1 år fra de ble ferdigstilt.</li>
              <li>Klikk <strong>Arkiver</strong> hvis du vil beholde en vurdering i lommeboken din evig (til du selv sletter den eller sletter kontoen).</li>
              <li>Attester for beståtte vurderinger finner du på Dashboard-siden under <strong>Kompetanseattester</strong>.</li>
            </ul>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-kf-blue-600" />
        </div>
      ) : error ? (
        <div className="p-4 bg-kf-error-50 border border-kf-error-200 rounded-lg text-kf-error-800">
          {error}
        </div>
      ) : assessments.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-lg border border-kf-gray-200">
          <ClipboardCheck className="w-16 h-16 text-kf-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-kf-gray-900 mb-2">Ingen vurderinger ennå</h3>
          <p className="text-kf-gray-600">
            Når sensor har ferdigstilt en vurdering, vil den vises her.
          </p>
        </div>
      ) : (
        <>
          {/* Aktive vurderinger */}
          <div className="space-y-3 mb-8">
            {active.length === 0 ? (
              <div className="text-center py-8 bg-white rounded-lg border border-kf-gray-200">
                <p className="text-kf-gray-600">
                  Ingen aktive vurderinger. Vis arkiverte for å se tidligere.
                </p>
              </div>
            ) : (
              active.map(a => <AssessmentCard key={a.id} a={a} onArchive={toggleArchive} navigate={navigate} />)
            )}
          </div>

          {/* Arkiverte vurderinger */}
          {archived.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-kf-gray-700 mb-3 flex items-center gap-2">
                <Archive className="w-5 h-5" />
                Arkivert ({archived.length})
              </h2>
              <div className="space-y-3">
                {archived.map(a => <AssessmentCard key={a.id} a={a} onArchive={toggleArchive} navigate={navigate} />)}
              </div>
            </div>
          )}

          {/* Toggle for å vise eldre (>1 år) */}
          <div className="mt-6 flex justify-center">
            <button
              onClick={() => setIncludeArchived(!includeArchived)}
              className="text-sm text-kf-blue-600 hover:text-kf-blue-800 flex items-center gap-2"
            >
              <ArchiveRestore className="w-4 h-4" />
              {includeArchived
                ? 'Skjul vurderinger utenfor 1-års vinduet'
                : 'Vis også eldre arkiverte (utenfor 1-års vinduet)'}
            </button>
          </div>
        </>
      )}

      <div className="mt-8">
        <Link to="/dashboard" className="text-sm text-kf-gray-600 hover:text-kf-gray-800">
          ← Tilbake til oversikt
        </Link>
      </div>
    </div>
  );
}

function AssessmentCard({
  a,
  onArchive,
  navigate,
}: {
  a: AssessmentListItem;
  onArchive: (id: string, archived: boolean) => void;
  navigate: (to: string) => void;
}) {
  return (
    <div className="bg-white rounded-lg border border-kf-gray-200 hover:border-kf-blue-400 transition-colors">
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-4">
          <button
            onClick={() => navigate(`/assessments/${a.id}`)}
            className="flex-1 text-left group"
          >
            <div className="flex items-start gap-3">
              {a.passed ? (
                <div className="p-2 bg-kf-success-100 rounded-lg flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-kf-success-700" />
                </div>
              ) : (
                <div className="p-2 bg-kf-error-100 rounded-lg flex-shrink-0">
                  <XCircle className="w-6 h-6 text-kf-error-700" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-kf-gray-900 group-hover:text-kf-blue-700 transition-colors">
                  {a.course_name}
                </h3>
                {a.organization_name && (
                  <p className="text-sm text-kf-gray-600">{a.organization_name}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-kf-gray-600">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {formatDate(a.finalized_at)}
                  </span>
                  <span>{modelLabel(a.model)}</span>
                  {a.model !== 'single_component' && (
                    <span>{a.component_count} komponenter</span>
                  )}
                </div>
                <div className="mt-2">
                  {a.passed ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-kf-success-50 text-kf-success-800 text-xs rounded border border-kf-success-200">
                      <CheckCircle2 className="w-3 h-3" /> Bestått
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-kf-error-50 text-kf-error-800 text-xs rounded border border-kf-error-200">
                      <XCircle className="w-3 h-3" /> Ikke bestått
                    </span>
                  )}
                  {a.archived && (
                    <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-kf-gray-100 text-kf-gray-700 text-xs rounded border border-kf-gray-300">
                      <Archive className="w-3 h-3" /> Arkivert
                    </span>
                  )}
                </div>
              </div>
            </div>
          </button>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => onArchive(a.id, a.archived)}
              className="hidden md:inline-flex items-center gap-1 px-3 py-1.5 text-xs text-kf-gray-600 hover:text-kf-gray-800 hover:bg-kf-gray-100 rounded border border-kf-gray-300"
              title={a.archived ? 'Fjern fra arkiv' : 'Arkiver evig'}
            >
              {a.archived ? (
                <>
                  <ArchiveRestore className="w-3.5 h-3.5" /> Fjern fra arkiv
                </>
              ) : (
                <>
                  <Archive className="w-3.5 h-3.5" /> Arkiver
                </>
              )}
            </button>
            <button
              onClick={() => navigate(`/assessments/${a.id}`)}
              className="p-2 text-kf-blue-600 hover:bg-kf-blue-50 rounded"
              aria-label="Se detaljer"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
